/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { computed, makeObservable, observable, action } from 'mobx';
import { FormatsJson } from '@veeam-vspc/models/web-controllers';

import type { LicenseEditionInfo, TimeZoneModel } from '@veeam-vspc/models/web-controllers';
import type { IdentityProvider } from '@veeam-vspc/models/rest';
import type * as themes from '@veeam/components/lib/themes';

import { PortalUser } from '../portal-user';

import type { NavigationBlockerPrompt, AppDataStoreParams, DefaultAppData } from './interfaces';

export class AppDataStore {

    readonly appMinWidth: number;
    readonly appMinHeight: number;
    readonly navMenuWidth: string;
    readonly portalLogoSrc: string;
    readonly exitConfigIconSrc: string;
    readonly exitButtonTextKey: string;

    readonly appCreationDate: string;
    readonly version: string;
    readonly isMaintenanceMode: boolean;
    readonly tokenProlongationPeriod: number;
    readonly sessionExpirationTime: number;
    readonly webHostName: string;
    readonly isDebug: boolean;
    readonly notifyVacCertificateNotInstalled: boolean;

    license: LicenseEditionInfo;
    licenseExpired: boolean;
    message: string | null;
    serviceTimeZone: TimeZoneModel;
    identityProviders: IdentityProvider[] | null;

    isAppFullyLoaded: boolean;

    @observable logged: boolean;
    @observable theme: keyof typeof themes;
    @observable locale: string;
    @observable formats: FormatsJson;

    @observable portalUser: PortalUser | null;
    @observable navigationBlockerPrompt: NavigationBlockerPrompt | null;

    @observable protected portalNameValue: string; // Be aware! It has getter/setter

    constructor(defaultAppData: DefaultAppData, appDataStoreParams: AppDataStoreParams = {}) {
        makeObservable(this);

        const {
            appMinWidth = 960,
            appMinHeight = 640,
            locale = 'en',
            fixedTheme,
            defaultTheme = 'blue',
            navMenuWidth = '240px',
            fixedPortalName,
            defaultPortalName,
            portalLogoSrc = '',
            exitConfigIconSrc,
            exitButtonTextKey,
            getCorrectProductTheme = (theme: keyof typeof themes) => theme,
        } = defaultAppData;
        const {
            loginData = {},
            identityProviders = null,
        } = appDataStoreParams;
        const {
            appCreationDate,
            version = process.env.VERSION,
            colorScheme,
            portalName: portalNameFromBackend,
            formats = {},
            license,
            licenseExpired,
            message,
            serviceTimeZone,
            user = null,
            isLocalAdmin = false,
            logged = false,
            tokenProlongationPeriod = 300,
            sessionExpirationTime = 3600,
            notifyVacCertificateNotInstalled = false,
            webHostName = '',
            isDebug = false,
        } = loginData;

        this.isAppFullyLoaded = false;
        this.appMinWidth = appMinWidth; // Use from default params
        this.appMinHeight = appMinHeight; // Use from default params
        this.navMenuWidth = navMenuWidth; // Use from default params
        this.portalLogoSrc = portalLogoSrc; // Use from default params
        this.exitConfigIconSrc = exitConfigIconSrc; // Use from default params
        this.exitButtonTextKey = exitButtonTextKey; // Use from default params

        this.appCreationDate = appCreationDate;
        this.logged = logged;
        this.version = version;
        this.portalName = fixedPortalName
            ?? (
                portalNameFromBackend
                ?? window.localStorage.getItem(process.env.PRODUCT_TITLE_KEY ?? 'veeam-rcop/vspc/general/title')
                ?? defaultPortalName
            )
            ?? 'Veeam Service Provider Console';
        this.isMaintenanceMode = isLocalAdmin;
        this.tokenProlongationPeriod = tokenProlongationPeriod;
        this.sessionExpirationTime = sessionExpirationTime;
        this.webHostName = webHostName;
        this.isDebug = isDebug;
        this.notifyVacCertificateNotInstalled = notifyVacCertificateNotInstalled;

        this.theme = getCorrectProductTheme(fixedTheme ?? (colorScheme || defaultTheme) as keyof typeof themes); // Use from default params
        this.locale = locale; // Use from default params

        this.formats = formats;
        /* tmp contract first */
        this.formats.netShortDate = formats.netShortDate || 'M/d/yyyy';
        this.formats.netShortTime = formats.netShortTime || 'h:mm tt';

        this.license = license;
        this.licenseExpired = licenseExpired;
        this.message = message;
        this.serviceTimeZone = serviceTimeZone;
        this.identityProviders = identityProviders;
        this.portalUser = user ? new PortalUser(user) : null;
    }

    @computed
    get portalName(): string {
        return this.portalNameValue;
    }

    set portalName(value: string) {
        this.portalNameValue = value;
        window.localStorage.setItem(process.env.PRODUCT_TITLE_KEY ?? 'veeam-rcop/vspc/general/title', value);
    }

    @action.bound
    logout(): void {
        // on logout we can reset this fields, but other should stay the same!
        this.logged = false;
        this.identityProviders = null;
    }

    @action.bound
    setIsAppFullyLoaded(value: boolean): void {
        this.isAppFullyLoaded = value;
    }

    @action.bound
    resetLoadableData(): void {
        this.setIsAppFullyLoaded(false);
        this.portalUser = null;
    }

}
